import React, { Component } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Rating,
  Box,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid, GridToolbarFilterButton } from '@mui/x-data-grid';
import { injectIntl } from 'react-intl';
import SimpleAutoComplete from '../../../../components/auto-complete/autocomplete';
import * as UserSkillsService from '../../../../services/userskills.service';
import Api from '../../../../services/api';

const NoDataRows = () => {
  return (
    <div
      className="MuiDataGrid-window"
      style={{ display: 'flex', justifyContent: 'center', top: 56, overflowY: 'auto' }}
    >
      Nenhuma habilidade cadastrada
    </div>
  );
};

class UserSkills extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      skills: [],
      types: [],
      organizations: [],
      categories: [],
      allOrganizations: [],
      dialogOpen: false,
      editingSkill: null,
      selectedType: null,
      selectedOrganization: null,
      selectedCategory: null,
      proficiencyLevel: 3,
      saving: false,
      loadingCategories: false,
      errorMessage: null,
      deleteDialogOpen: false,
      skillToDelete: null
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.userId !== this.props.userId && this.props.userId) {
      this.loadSkills();
    }
    // Atualizar lista de organizações quando as organizações do usuário mudarem
    if (prevProps.userOrganizations !== this.props.userOrganizations) {
      this.updateAvailableOrganizations();
    }
  }

  loadData = async () => {
    await Promise.all([this.loadSkills(), this.loadTypes(), this.loadAllOrganizations()]);
  };

  loadSkills = async () => {
    const { userId } = this.props;
    if (!userId) {
      this.setState({ loading: false });
      return;
    }

    try {
      const result = await UserSkillsService.getByUserId(userId);
      if (result && result.success !== false && result.data) {
        const skillsData = Array.isArray(result.data) ? result.data : [];
        const skills = skillsData.map((skill, index) => ({
          id: skill.id || index,
          typeId: skill.typeid || skill.typeId,
          typeName: skill.typename || skill.typeName || '-',
          organizationId: skill.organizationid || skill.organizationId,
          organizationName: skill.organizationname || skill.organizationName || '-',
          organizationCategoryId: skill.organizationcategoryid || skill.organizationCategoryId,
          categoryName: skill.categoryname || skill.categoryName || '-',
          proficiencyLevel: skill.proficiencylevel || skill.proficiencyLevel || 1
        }));
        this.setState({ skills, loading: false });
      } else {
        this.setState({ skills: [], loading: false });
      }
    } catch (error) {
      console.error('Error loading skills:', error);
      this.setState({ skills: [], loading: false });
    }
  };

  loadTypes = async () => {
    try {
      const result = await Api.get('/types/selectionmodel');
      if (result && result.data && result.data.data) {
        this.setState({ types: result.data.data });
      }
    } catch (error) {
      console.error('Error loading types:', error);
    }
  };

  loadAllOrganizations = async () => {
    try {
      const result = await Api.get('/organizations/selectionmodel');
      if (result && result.data && result.data.data) {
        this.setState({ allOrganizations: result.data.data }, () => {
          this.updateAvailableOrganizations();
        });
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  };

  updateAvailableOrganizations = () => {
    const { userOrganizations } = this.props;
    const { allOrganizations } = this.state;

    // Se o usuário tem organizações selecionadas no bloco "Organizações", filtrar
    if (userOrganizations && userOrganizations.length > 0) {
      const userOrgIds = userOrganizations.map(org => org.id || org.Id);
      const filteredOrgs = allOrganizations.filter(org => userOrgIds.includes(org.id));
      this.setState({ organizations: filteredOrgs });
    } else {
      // Se não tem organizações selecionadas, mostrar todas
      this.setState({ organizations: allOrganizations });
    }
  };

  loadCategoriesByOrganization = async (organizationId) => {
    if (!organizationId) {
      this.setState({ categories: [] });
      return;
    }

    this.setState({ loadingCategories: true });
    try {
      const result = await Api.get(`/organizationcategories/selectionmodel/createcalled/organization/${organizationId}`);
      if (result && result.data && result.data.data) {
        this.setState({ categories: result.data.data, loadingCategories: false });
      } else {
        this.setState({ categories: [], loadingCategories: false });
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback: tentar carregar todas as categorias
      try {
        const fallbackResult = await Api.get('/organizationcategories/selectionmodel');
        if (fallbackResult && fallbackResult.data && fallbackResult.data.data) {
          // Filtrar categorias pela organização selecionada
          const filteredCategories = fallbackResult.data.data.filter(
            cat => cat.organizationid === organizationId || cat.organizationId === organizationId
          );
          this.setState({ categories: filteredCategories, loadingCategories: false });
        } else {
          this.setState({ categories: [], loadingCategories: false });
        }
      } catch (fallbackError) {
        console.error('Error loading categories fallback:', fallbackError);
        this.setState({ categories: [], loadingCategories: false });
      }
    }
  };

  handleOpenDialog = async (skill = null) => {
    if (skill) {
      // Usar comparação flexível de IDs (== em vez de ===) para lidar com tipos diferentes
      const type = this.state.types.find(t => t.id == skill.typeId);
      const organization = this.state.organizations.find(o => o.id == skill.organizationId);

      // Carregar categorias da organização antes de abrir o dialog
      if (organization && organization.id) {
        try {
          const result = await Api.get(`/organizationcategories/selectionmodel/createcalled/organization/${organization.id}`);
          const categories = (result && result.data && result.data.data) ? result.data.data : [];
          const category = categories.find(c => c.id == skill.organizationCategoryId);

          this.setState({
            dialogOpen: true,
            editingSkill: skill,
            selectedType: type || null,
            selectedOrganization: organization || null,
            selectedCategory: category || null,
            categories: categories,
            proficiencyLevel: skill.proficiencyLevel || 3,
            loadingCategories: false
          });
        } catch (error) {
          console.error('Error loading categories for edit:', error);
          this.setState({
            dialogOpen: true,
            editingSkill: skill,
            selectedType: type || null,
            selectedOrganization: organization || null,
            selectedCategory: null,
            categories: [],
            proficiencyLevel: skill.proficiencyLevel || 3,
            loadingCategories: false
          });
        }
      } else {
        this.setState({
          dialogOpen: true,
          editingSkill: skill,
          selectedType: type || null,
          selectedOrganization: null,
          selectedCategory: null,
          categories: [],
          proficiencyLevel: skill.proficiencyLevel || 3
        });
      }
    } else {
      this.setState({
        dialogOpen: true,
        editingSkill: null,
        selectedType: null,
        selectedOrganization: null,
        selectedCategory: null,
        categories: [],
        proficiencyLevel: 3
      });
    }
  };

  handleCloseDialog = () => {
    this.setState({
      dialogOpen: false,
      editingSkill: null,
      selectedType: null,
      selectedOrganization: null,
      selectedCategory: null,
      categories: [],
      proficiencyLevel: 3,
      saving: false,
      errorMessage: null
    });
  };

  handleSaveSkill = async () => {
    const { userId, hirerId } = this.props;
    const { editingSkill, selectedType, selectedOrganization, selectedCategory, proficiencyLevel } = this.state;

    if (!selectedType && !selectedCategory) {
      this.setState({ errorMessage: 'Selecione pelo menos um tipo ou categoria' });
      return;
    }

    this.setState({ saving: true, errorMessage: null });

    const skillData = {
      userId,
      hirerId,
      typeId: selectedType?.id || null,
      organizationCategoryId: selectedCategory?.id || null,
      proficiencyLevel
    };

    try {
      let result;
      if (editingSkill) {
        skillData.id = editingSkill.id;
        result = await UserSkillsService.update(skillData);
      } else {
        result = await UserSkillsService.create(skillData);
      }

      // Verificar se houve erro na resposta
      if (result && result.errors && result.errors.length > 0) {
        this.setState({
          saving: false,
          errorMessage: result.errors[0]
        });
        return;
      }

      // Verificar se a resposta indica sucesso
      if (result && result.success === false) {
        this.setState({
          saving: false,
          errorMessage: result.errors?.[0] || 'Erro ao salvar habilidade'
        });
        return;
      }

      this.handleCloseDialog();
      this.loadSkills();
    } catch (error) {
      console.error('Error saving skill:', error);
      const errorMsg = error.response?.data?.errors?.[0] || 'Erro ao salvar habilidade';
      this.setState({ saving: false, errorMessage: errorMsg });
    }
  };

  handleOpenDeleteDialog = (skill) => {
    this.setState({ deleteDialogOpen: true, skillToDelete: skill });
  };

  handleCloseDeleteDialog = () => {
    this.setState({ deleteDialogOpen: false, skillToDelete: null });
  };

  handleConfirmDelete = async () => {
    const { skillToDelete } = this.state;
    if (!skillToDelete) return;

    try {
      await UserSkillsService.remove(skillToDelete.id);
      this.handleCloseDeleteDialog();
      this.loadSkills();
    } catch (error) {
      console.error('Error deleting skill:', error);
    }
  };

  handleTypeChange = (selected) => {
    this.setState({ selectedType: selected });
  };

  handleOrganizationChange = (selected) => {
    this.setState({
      selectedOrganization: selected,
      selectedCategory: null // Limpar categoria ao mudar organização
    });

    // Carregar categorias da organização selecionada
    if (selected && selected.id) {
      this.loadCategoriesByOrganization(selected.id);
    } else {
      this.setState({ categories: [] });
    }
  };

  handleCategoryChange = (selected) => {
    this.setState({ selectedCategory: selected });
  };

  render() {
    const { intl, classes, disabled } = this.props;
    const {
      loading,
      skills,
      types,
      organizations,
      categories,
      dialogOpen,
      editingSkill,
      selectedType,
      selectedOrganization,
      selectedCategory,
      proficiencyLevel,
      saving,
      loadingCategories,
      errorMessage,
      deleteDialogOpen,
      skillToDelete
    } = this.state;

    const columns = [
      {
        headerName: intl.formatMessage({ id: 'skill.type' }),
        field: 'typeName',
        width: 180
      },
      {
        headerName: intl.formatMessage({ id: 'organization' }),
        field: 'organizationName',
        width: 180
      },
      {
        headerName: intl.formatMessage({ id: 'skill.category' }),
        field: 'categoryName',
        width: 180
      },
      {
        headerName: intl.formatMessage({ id: 'skill.proficiency.level' }),
        field: 'proficiencyLevel',
        width: 150,
        renderCell: (params) => (
          <Rating value={parseInt(params.value, 10) || 1} readOnly max={5} />
        )
      },
      {
        headerName: '',
        field: 'actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Box>
            <IconButton
              size="small"
              onClick={() => this.handleOpenDialog(params.row)}
              disabled={disabled}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => this.handleOpenDeleteDialog(params.row)}
              disabled={disabled}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )
      }
    ];

    return (
      <>
        <Accordion style={{ marginTop: '6px', marginBottom: '10px' }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="user-skills-content"
            id="user-skills-header"
          >
            <Typography className={classes?.heading}>
              {intl.formatMessage({ id: 'user.skills' })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {loading ? (
              <Grid container justifyContent="center">
                <CircularProgress />
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '10px'
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => this.handleOpenDialog()}
                    disabled={disabled}
                  >
                    {intl.formatMessage({ id: 'skill.add' })}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <div style={{ height: 350 }}>
                    <DataGrid
                      style={{ fontSize: 14, color: '#000' }}
                      className="DataGridStyle"
                      components={{
                        NoRowsOverlay: NoDataRows,
                        Toolbar: GridToolbarFilterButton
                      }}
                      rows={skills}
                      columns={columns}
                      rowsPerPageOptions={[5, 10, 25]}
                      disableSelectionOnClick
                    />
                  </div>
                </Grid>
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>

        <Dialog open={dialogOpen} onClose={this.handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingSkill
              ? intl.formatMessage({ id: 'skill.edit' })
              : intl.formatMessage({ id: 'skill.add' })}
            <IconButton
              aria-label="close"
              onClick={this.handleCloseDialog}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {errorMessage && (
                <Grid item xs={12}>
                  <Alert severity="error" onClose={() => this.setState({ errorMessage: null })}>
                    {errorMessage}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <SimpleAutoComplete
                  label={intl.formatMessage({ id: 'skill.type' })}
                  options={types}
                  stateName="selectedType"
                  changeSelect={(name, value) => this.handleTypeChange(value)}
                  selected={selectedType}
                />
              </Grid>
              <Grid item xs={12}>
                <SimpleAutoComplete
                  label={intl.formatMessage({ id: 'organization' })}
                  options={organizations}
                  stateName="selectedOrganization"
                  changeSelect={(name, value) => this.handleOrganizationChange(value)}
                  selected={selectedOrganization}
                />
              </Grid>
              <Grid item xs={12}>
                {loadingCategories ? (
                  <Grid container justifyContent="center" style={{ padding: '10px' }}>
                    <CircularProgress size={24} />
                  </Grid>
                ) : (
                  <SimpleAutoComplete
                    label={intl.formatMessage({ id: 'skill.category' })}
                    options={categories}
                    stateName="selectedCategory"
                    changeSelect={(name, value) => this.handleCategoryChange(value)}
                    selected={selectedCategory}
                    disabled={!selectedOrganization}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography component="legend">
                  {intl.formatMessage({ id: 'skill.proficiency.level' })}
                </Typography>
                <Rating
                  name="proficiency"
                  value={proficiencyLevel}
                  onChange={(event, newValue) => {
                    this.setState({ proficiencyLevel: newValue || 1 });
                  }}
                  max={5}
                  size="large"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseDialog} disabled={saving}>
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button
              onClick={this.handleSaveSkill}
              variant="contained"
              color="primary"
              disabled={saving || (!selectedType && !selectedCategory)}
            >
              {saving ? <CircularProgress size={24} /> : intl.formatMessage({ id: 'save' })}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmação de exclusão */}
        <Dialog open={deleteDialogOpen} onClose={this.handleCloseDeleteDialog}>
          <DialogTitle>
            {intl.formatMessage({ id: 'confirm' })}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {intl.formatMessage({ id: 'sure.delete' })}
            </Typography>
            {skillToDelete && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {skillToDelete.typeName !== '-' ? `${intl.formatMessage({ id: 'skill.type' })}: ${skillToDelete.typeName}` : ''}
                {skillToDelete.typeName !== '-' && skillToDelete.categoryName !== '-' ? ' | ' : ''}
                {skillToDelete.categoryName !== '-' ? `${intl.formatMessage({ id: 'skill.category' })}: ${skillToDelete.categoryName}` : ''}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseDeleteDialog}>
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button onClick={this.handleConfirmDelete} color="error" variant="contained">
              {intl.formatMessage({ id: 'delete' })}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

export default injectIntl(UserSkills, { forwardRef: true });
